import moment from 'moment-timezone';
import {type Moment} from 'moment-timezone';

/**
 * A potentially open ended date range with an inclusive from date and exclusive end date.
 *
 * Aligns with date ranges of plans in the backend.
 */
export type DateRangeExclusive = {
    fromInclusive: Moment;
    toExclusive: Moment | null;
};

/**
 * A potentially open ended date range with inclusive from and to dates.
 *
 * Aligns with the users perception of a date range.
 */
export type DateRangeInclusive = {
    fromInclusive: Moment;
    toInclusive: Moment | null;
};

export const convertExclusiveToInclusive = (range: DateRangeExclusive): DateRangeInclusive => {
    const result = {
        fromInclusive: range.fromInclusive,
        toInclusive: range.toExclusive !== null ? moment(range.toExclusive).add(-1, 'day') : null,
    };
    return result;
};
export const convertInclusiveToExclusive = (range: DateRangeInclusive): DateRangeExclusive => {
    return {
        fromInclusive: range.fromInclusive,
        toExclusive: range.toInclusive !== null ? moment(range.toInclusive).add(1, 'day') : null,
    };
};

/**
 * True iff two date ranges overlap by at least one day.
 * @returns True iff `range1` and `range2` overlap.
 */
export const testOverlap = (range1: DateRangeExclusive, range2: DateRangeExclusive): boolean => {
    if (range1.fromInclusive.isBefore(range2.fromInclusive)) {
        return range1.toExclusive === null || range1.toExclusive.isAfter(range2.fromInclusive);
    } else {
        return range2.toExclusive === null || range2.toExclusive.isAfter(range1.fromInclusive);
    }
};

/**
 * Tests if one date range ends strictly before another date range ends.
 * @param before The earlier date range.
 * @param after The later date range.
 * @returns True iff `before` ends at least one day before `after`.
 */
export const testEndsBefore = (before: DateRangeExclusive, after: DateRangeExclusive): boolean => {
    return (
        before.toExclusive !== null && (after.toExclusive === null || before.toExclusive.isBefore(after.toExclusive))
    );
};

/**
 * Clips a date range such that it does not start any earlier than a given
 * cut date. I.e. if the date range starts before the cut date, its start
 * date is moved to the cut date.
 *
 * Assumes that the cut date is before or in the the date range.
 * @param range The date range to cut.
 * @param cut The cut date.
 * @returns The potentially clipped date range.
 */
export const removeDateRangePrefix = (range: DateRangeExclusive, cut: Moment): DateRangeExclusive => {
    if (range.fromInclusive.isBefore(cut) && (range.toExclusive === null || cut.isBefore(range.toExclusive))) {
        return {fromInclusive: cut, toExclusive: range.toExclusive};
    } else {
        return range;
    }
};
